import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import { dashboardAPI } from '../../../../services/dashboardService';

const DailyOperations = ({ onBack }) => {
    const { clinicId } = useParams();
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({ date: '' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                setLoading(true);
                const params = {};
                if (filters.date) params.date = filters.date;

                const response = await dashboardAPI.getDailyOperationsMetrics(clinicId, params);
                setData(response.data);
            } catch (err) {
                setError(err.message || 'Failed to load metrics');
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchMetrics();
        }
    }, [clinicId, filters]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ViewHeader title="Daily Operation Control" subtitle="Loading..." onBack={onBack} />
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading daily data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ViewHeader title="Daily Operation Control" subtitle="Error" onBack={onBack} />
                <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
            </div>
        );
    }

    if (!data) return null;

    const { weekly_appointments, inpatient_monitoring, key_metrics, clients } = data;

    const weeklyData = weekly_appointments.map(item => ({
        name: item.day,
        value: item.appointments
    }));

    const inpatientMetrics = [
        { title: 'Patients in isolation', value: String(inpatient_monitoring.patients_in_isolation) },
        { title: 'Missed treatment', value: String(inpatient_monitoring.missed_treatment) },
        { title: 'Discharged today', value: String(inpatient_monitoring.discharged_today) },
        { title: 'ICU patients', value: String(inpatient_monitoring.icu_patients) },
    ];

    const recentPatients = inpatient_monitoring.last_updated_patients.map(p => ({
        pet: p.pet_name,
        diagnosis: p.diagnosis,
        exam: p.physical_exam
    }));

    const displayKeyMetrics = [
        { title: "Today's appointment", value: String(key_metrics.todays_appointment) },
        { title: 'Walks in (today)', value: String(key_metrics.walks_in) },
        { title: 'Outpatients', value: String(key_metrics.outpatients) },
        { title: 'Inpatients', value: String(key_metrics.inpatients) },
    ];

    const clientMetrics = [
        { title: 'No show rate', value: `${clients.no_show_rate}%`, unit: '' },
        { title: 'New clients', value: String(clients.new_clients) },
        { title: 'Repeat client', value: String(clients.repeat_clients) },
    ];

    const subtitle = filters.date
        ? new Date(filters.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : 'Today';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ViewHeader
                title="Daily Operation Control"
                subtitle={subtitle}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="date"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Row 1: Weekly chart | Inpatient monitoring + table */}
            <div className="clinic-dashboard-section-row">
                {/* Weekly appointments chart */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Weekly appointments</div>
                    <div className="clinic-dashboard-chart-wrap">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={weeklyData} barSize={24} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    dy={10}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Bar animationBegin={600} dataKey="value" fill="#00D4B3" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Inpatient monitoring + recent patients */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Inpatient monitoring</div>
                    <div className="clinic-dashboard-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '16px' }}>
                        {inpatientMetrics.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>

                    <div className="clinic-dashboard-section-title" style={{ fontSize: '14px', marginBottom: '10px' }}>Last updated patients (status)</div>
                    {recentPatients.length > 0 ? (
                        <div className="clinic-dashboard-table-wrap">
                            <table className="clinic-dashboard-table">
                                <thead>
                                    <tr>
                                        <th>Pet</th>
                                        <th>SOAP Diagnosis</th>
                                        <th>Physical exam of the day</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentPatients.map((row, i) => (
                                        <tr key={i}>
                                            <td>{row.pet}</td>
                                            <td>{row.diagnosis}</td>
                                            <td>{row.exam}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <EmptyState message="No recent patient updates" />
                    )}
                </div>
            </div>

            {/* Row 2: Key metrics | Clients today */}
            <div className="clinic-dashboard-section-row">
                {/* Key metrics */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                        {displayKeyMetrics.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                {/* Clients today */}
                <div className="clinic-dashboard-section-card" style={{ animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Clients (today)</div>
                    <div className="clinic-dashboard-kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        {clientMetrics.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DailyOperations;
